import * as React from "react"
import { Avatar, Box, Chip, Typography, Tooltip, IconButton } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { tableData, isLoadingFilters, mapDrawerProduct, compareSite, matchCompareModal } from "../@signals"

// Column order in data array:
// 0: site_name, 1: brand, 2: product (object), 3: product_id (hidden),
// 4: ean (hidden), 5: reference (hidden), 6: category, 7: product_url (hidden),
// 8: last_seen, 9: matches, 10: match_status

function formatRelativeDate(dateString) {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const now = new Date()
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
    if (diffDays < 0) return 'Future'
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays}d ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
    if (diffDays < 60) return '1m ago'
    return `${Math.floor(diffDays / 30)}m ago`
}

export const brandColumn = () => ({
    name: 'brand',
    label: 'Brand',
    options: {
        filter: true,
        sort: true,
        searchable: true,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value) => (
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#6366f1' }}>
                {value || '-'}
            </Typography>
        )
    }
})

export const productColumn = () => ({
    name: 'product',
    label: 'Product',
    options: {
        filter: false,
        sort: false,
        searchable: false,
        setCellHeaderProps: () => ({
            align: 'left',
            sx: { backgroundColor: '#fef3c7' }
        }),
        setCellProps: () => ({ align: 'left' }),
        customBodyRender: (product) => {
            if (!product) return '-'
            return (
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Avatar
                        src={product.image_url}
                        variant="rounded"
                        sx={{
                            width: 70,
                            height: 70,
                            bgcolor: '#f3f4f6',
                            '& img': { objectFit: 'contain' },
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                                transform: 'scale(1.05)',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                            }
                        }}
                    >
                        <IconSeeker icon="image" sx={{ color: '#9ca3af' }} />
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.5 }}>
                            <Tooltip title={product.name || '-'} placement="top">
                                {product.product_url ? (
                                    <a
                                        href={product.product_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: 4 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 600,
                                                wordBreak: 'break-word',
                                                lineHeight: 1.3,
                                                color: 'text.primary',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                            {product.name || '-'}
                                        </Typography>
                                        <IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 14, color: '#6366f1', flexShrink: 0, mt: 0.25 }} />
                                    </a>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            wordBreak: 'break-word',
                                            lineHeight: 1.3
                                        }}
                                    >
                                        {product.name || '-'}
                                    </Typography>
                                )}
                            </Tooltip>
                        </Box>
                        {product.variant_raw && (
                            <Tooltip title={product.variant_raw} placement="top">
                                <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                        display: 'inline',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        backgroundColor: '#e0e7ff',
                                        color: '#4338ca',
                                        mt: 0.5,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        lineHeight: 1.8,
                                        boxDecorationBreak: 'clone',
                                        WebkitBoxDecorationBreak: 'clone'
                                    }}
                                >
                                    {product.variant_raw}
                                </Typography>
                            </Tooltip>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                ID: {product.product_id || '-'}
                            </Typography>
                            {product.ean && (
                                <Tooltip title="EAN">
                                    <Chip
                                        icon={<IconSeeker icon="barcode" sx={{ fontSize: 12 }} />}
                                        label={product.ean}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                </Tooltip>
                            )}
                            {product.reference && (
                                <Tooltip title="Reference">
                                    <Chip
                                        icon={<IconSeeker icon="tag" sx={{ fontSize: 12 }} />}
                                        label={product.reference}
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Box>
            )
        }
    }
})

export const productIdColumn = () => ({
    name: 'product_id',
    label: 'Product ID',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true
    }
})

export const siteColumn = () => ({
    name: 'site_name',
    label: 'Site',
    options: {
        filter: true,
        sort: true,
        searchable: false,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value) => (
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {value || '-'}
            </Typography>
        )
    }
})

export const idSiteColumn = () => ({
    name: 'id_site',
    label: 'id_site',
    options: { filter: false, sort: false, display: false, searchable: false, viewColumns: false }
})

export const idCustomerColumn = () => ({
    name: 'id_customer',
    label: 'id_customer',
    options: { filter: false, sort: false, display: false, searchable: false, viewColumns: false }
})

export const eanColumn = () => ({
    name: 'ean',
    label: 'EAN',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true
    }
})

export const referenceColumn = () => ({
    name: 'reference',
    label: 'Reference',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: true
    }
})

export const categoryColumn = () => ({
    name: 'category',
    label: 'Category',
    options: {
        filter: true,
        sort: true,
        searchable: false
    }
})

export const productUrlColumn = () => ({
    name: 'product_url',
    label: 'URL',
    options: {
        filter: false,
        sort: false,
        display: false,
        searchable: false,
        viewColumns: false
    }
})

export const lastSeenColumn = () => ({
    name: 'last_seen',
    label: 'Last Seen',
    options: {
        filter: false,
        sort: true,
        searchable: false,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value) => (
            <Tooltip title={value || '-'}>
                <Typography
                    variant="caption"
                    sx={{
                        color: '#059669',
                        fontWeight: 500,
                        cursor: 'help'
                    }}
                >
                    {formatRelativeDate(value)}
                </Typography>
            </Tooltip>
        )
    }
})

export const matchesColumn = () => ({
    name: 'matches',
    label: 'Match',
    options: {
        filter: false,
        sort: false,
        searchable: false,
        setCellHeaderProps: () => ({
            align: 'left',
            sx: { backgroundColor: '#d1fae5' }
        }),
        setCellProps: () => ({ align: 'left' }),
        customBodyRender: (value, tableMeta) => {
            const matches = value || []
            const productId = tableMeta.rowData[3]
            const idSite = tableMeta.rowData[4]
            const idCustomer = tableMeta.rowData[5]
            const hasCompareSite = !!compareSite.value

            const sourceProduct = {
                ...tableMeta.rowData[2],
                id_site: idSite,
                id_customer: idCustomer,
                site_name: tableMeta.rowData[0]
            }

            const openCompare = (matchIndex) => {
                matchCompareModal.value = { product: sourceProduct, matches, index: matchIndex }
            }

            const handleUnmap = (targetIdSite, targetProductId) => {
                // Update local state immediately
                tableData.value = tableData.value.map(item =>
                    item.id_site === idSite && item.product_id === productId
                        ? { ...item, matches: (item.matches || []).filter(m => !(m.id_site === targetIdSite && m.product_id === targetProductId)) }
                        : item
                )
                // Call API in background
                postSubmit(`${BACKEND_APIS.RETAILER_PRODUCTS}/unmap`, {
                    id_customer: idCustomer,
                    id_site: idSite,
                    product_id: productId,
                    target_id_site: targetIdSite,
                    target_product_id: targetProductId
                }).catch(err => console.error('Error unmapping:', err))
            }

            if (matches.length === 0) {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 1, color: '#9ca3af' }}>
                        <IconSeeker icon="link-slash" sx={{ mr: 1, fontSize: 16 }} />
                        <Typography variant="body2">{hasCompareSite ? 'No match' : 'No matches'}</Typography>
                    </Box>
                )
            }

            // Compare site mode: single flat match display
            if (hasCompareSite) {
                const m = matches[0]
                return (
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 0.5 }}>
                        <Avatar
                            src={m.image_url}
                            variant="rounded"
                            onClick={(e) => { e.stopPropagation(); openCompare(0) }}
                            sx={{ width: 50, height: 50, bgcolor: '#f3f4f6', flexShrink: 0, cursor: 'pointer', '& img': { objectFit: 'contain' }, '&:hover': { boxShadow: '0 0 0 2px #10b981' } }}
                        >
                            <IconSeeker icon="image" sx={{ color: '#d1d5db', fontSize: 16 }} />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                <Tooltip title={m.name || m.product_id} placement="top">
                                    {m.product_url ? (
                                        <a href={m.product_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', '&:hover': { textDecoration: 'underline' } }}>
                                                {m.name || m.product_id}
                                            </Typography>
                                            <IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 10, color: '#6366f1', flexShrink: 0, mt: 0.25 }} />
                                        </a>
                                    ) : (
                                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                            {m.name || m.product_id}
                                        </Typography>
                                    )}
                                </Tooltip>
                            </Box>
                            {m.variant_raw && (
                                <Typography component="span" variant="caption" sx={{ display: 'inline', fontSize: '0.7rem', fontWeight: 600, backgroundColor: '#e0e7ff', color: '#4338ca', px: 0.75, py: 0.15, borderRadius: 0.5 }}>
                                    {m.variant_raw}
                                </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.65rem', display: 'block', mt: 0.25 }}>
                                {m.product_id}
                            </Typography>
                        </Box>
                        <Tooltip title="Remove match">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUnmap(m.id_site, m.product_id) }} sx={{ flexShrink: 0, color: '#d1d5db', '&:hover': { color: '#ef4444' } }}>
                                <IconSeeker icon="xmark" sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }

            // Default: multi-match list
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.5, maxHeight: 200, overflowY: 'auto' }}>
                    {matches.map((m, i) => (
                        <Box key={i} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', borderBottom: i < matches.length - 1 ? '1px solid #f3f4f6' : 'none', pb: i < matches.length - 1 ? 1 : 0 }}>
                            <Avatar src={m.image_url} variant="rounded" onClick={(e) => { e.stopPropagation(); openCompare(i) }} sx={{ width: 40, height: 40, bgcolor: '#f3f4f6', flexShrink: 0, cursor: 'pointer', '& img': { objectFit: 'contain' }, '&:hover': { boxShadow: '0 0 0 2px #10b981' } }}>
                                <IconSeeker icon="image" sx={{ color: '#d1d5db', fontSize: 16 }} />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                    <Tooltip title={m.name || m.product_id} placement="top">
                                        {m.product_url ? (
                                            <a href={m.product_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ textDecoration: 'none', display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.3, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', '&:hover': { textDecoration: 'underline' } }}>
                                                    {m.name || m.product_id}
                                                </Typography>
                                                <IconSeeker icon="arrow-up-right-from-square" sx={{ fontSize: 10, color: '#6366f1', flexShrink: 0, mt: 0.25 }} />
                                            </a>
                                        ) : (
                                            <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                                {m.name || m.product_id}
                                            </Typography>
                                        )}
                                    </Tooltip>
                                </Box>
                                {m.variant_raw && (
                                    <Typography component="span" variant="caption" sx={{ display: 'inline', fontSize: '0.65rem', fontWeight: 600, backgroundColor: '#e0e7ff', color: '#4338ca', px: 0.75, py: 0.15, borderRadius: 0.5 }}>
                                        {m.variant_raw}
                                    </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.25 }}>
                                    <Typography variant="caption" sx={{ color: '#10b981', fontWeight: 500, fontSize: '0.65rem' }}>
                                        {m.site_name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.6rem' }}>
                                        {m.product_id}
                                    </Typography>
                                </Box>
                            </Box>
                            <Tooltip title="Remove match">
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleUnmap(m.id_site, m.product_id) }} sx={{ flexShrink: 0, color: '#d1d5db', '&:hover': { color: '#ef4444' } }}>
                                    <IconSeeker icon="xmark" sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}
                </Box>
            )
        }
    }
})

export const matchStatusColumn = () => ({
    name: 'match_status',
    label: 'Status',
    options: {
        filter: true,
        filterType: 'checkbox',
        sort: true,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value, tableMeta) => {
            const matches = tableMeta.rowData[11] || []
            const count = matches.length
            const hasMatch = count > 0
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Chip
                        label={hasMatch ? 'Matched' : 'Pending'}
                        size="small"
                        sx={{
                            fontSize: '0.7rem',
                            height: 22,
                            fontWeight: 600,
                            bgcolor: hasMatch ? '#d1fae5' : '#fef3c7',
                            color: hasMatch ? '#065f46' : '#92400e',
                        }}
                    />
                    {hasMatch && (
                        <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem' }}>
                            {count} {count === 1 ? 'match' : 'matches'}
                        </Typography>
                    )}
                </Box>
            )
        }
    }
})

export const priceScoreColumn = () => ({
    name: 'price_score',
    label: 'Score',
    options: {
        filter: false,
        sort: true,
        searchable: false,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center' }),
        customBodyRender: (value) => {
            if (value === null || value === undefined) {
                return <Typography variant="caption" sx={{ color: '#d1d5db' }}>-</Typography>
            }

            const pct = Math.round(value * 100)
            let bgcolor, color
            if (value >= 0.8) {
                bgcolor = '#d1fae5'
                color = '#065f46'
            } else if (value >= 0.5) {
                bgcolor = '#fef3c7'
                color = '#92400e'
            } else {
                bgcolor = '#fee2e2'
                color = '#991b1b'
            }

            return (
                <Chip
                    label={`${pct}%`}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: 22, fontWeight: 600, bgcolor, color }}
                />
            )
        }
    }
})

export const actionsColumn = () => ({
    name: 'actions',
    label: ' ',
    options: {
        filter: false,
        sort: false,
        searchable: false,
        viewColumns: false,
        setCellHeaderProps: () => ({ align: 'center' }),
        setCellProps: () => ({ align: 'center', style: { width: 50 } }),
        customBodyRender: (_, tableMeta) => {
            const product = tableMeta.rowData[2]
            const idSite = tableMeta.rowData[4]
            const idCustomer = tableMeta.rowData[5]

            const handleOpen = (e) => {
                e.stopPropagation()
                mapDrawerProduct.value = {
                    ...product,
                    id_site: idSite,
                    id_customer: idCustomer
                }
            }

            return (
                <Tooltip title="Add match">
                    <IconButton size="small" onClick={handleOpen} sx={{ color: '#3b82f6' }}>
                        <IconSeeker icon="plus" sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            )
        }
    }
})
